import { put, takeLatest, select } from "redux-saga/effects";
import {
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  loadThreadMessagesRequest,
  loadThreadMessagesSuccess,
  loadThreadMessagesFailure
} from "./chatbotSlice";
import { addMessage } from "../messages/messagesSlice";
import { wizardURL } from "../../utils/config";
import TokenManager from "../../utils/tokenManager"; 

function* sendMessageSaga(action) {
  try {
    const { message, metadata = null, threadId: payloadThreadId, context = null } = action.payload;
    
    const token = TokenManager.getAccessToken(); 
    if (!token) throw new Error("Not authenticated");

    const chatbotState = yield select(state => state.chatbot);
    const stateThreadId = chatbotState?.currentThreadId;
    
    const currentThreadId = payloadThreadId !== undefined && payloadThreadId !== null 
      ? payloadThreadId 
      : stateThreadId;

    if (!currentThreadId) {
      throw new Error("Cannot send message without a threadId.");
    }

    // Send message with file references and secure token
    const response = yield fetch(`${wizardURL}/wizard/chat/dss`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}` 
      },
      body: JSON.stringify({
        thread_id: currentThreadId,
        message,
        document_ids: [],
        project_context: context ? {
          goals: context.goals || "",
          challenges: context.challenges || "",
          resources: context.resources || "",
          description: context.description || "",
          related_fields: context.relatedFields || [],
        } : null,
      }),
    });


    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = yield response.json();
    
    const sources = Array.isArray(data.sources) ? data.sources : [];
    
    const mapSourceMetadata = (src) => ({
      text: src.title || "Untitled source",
      source_url: null,
      source_type: "solution", 
      source_reference: {
        id: src.id || null,
        entry_id: src.entry_id || src.id || null, 
        title: src.title || "Untitled source",
        brief_description: src.brief_description || "No description available.",
        source_type: "solution" 
      },
    });

    const knowledgeUsed = sources.map(mapSourceMetadata);

    const userMessage = {
      id: Date.now() + "_user",
      text: message,
      sender: "user",
      timestamp: new Date().toISOString(),
      files: [],
    };

    const assistantMessage = {
      id: Date.now() + "_assistant",
      text: data.answer,
      sender: "bot",
      timestamp: new Date().toISOString(),
      metadata: {
        knowledge_used: sources.map(mapSourceMetadata),
        ...(data.cypher_used ? { cypher_used: data.cypher_used } : {}),
      },
    };

    yield put(addMessage({
      threadId: currentThreadId,
      message: {
        id: userMessage.id,
        text: message,
        sender: "user",
        timestamp: userMessage.timestamp,
        metadata: metadata || {},
      },
    }));

    yield put(addMessage({
      threadId: currentThreadId,
      message: assistantMessage,
    }));

    yield put(sendMessageSuccess({
      userMessage: {
        id: userMessage.id,
        text: message,
        sender: "user",
        timestamp: userMessage.timestamp,
        metadata: metadata || {},
      },
      assistantResponse: {
        id: assistantMessage.id,
        text: data.answer,
        sender: "bot",
        timestamp: assistantMessage.timestamp,
        metadata: {
          knowledge_used: knowledgeUsed,
          cypher_used: data.cypher_used || null,
        },
      },
      threadId: currentThreadId,
    }));
  } catch (error) {
    console.error("Error in sendMessageSaga:", error);
    yield put(sendMessageFailure(error.message));
  }
}

function* loadThreadMessagesSaga(action) {
  try {
    const { threadId } = action.payload;
    const token = TokenManager.getAccessToken(); 
    
    if (!token) throw new Error("Not authenticated");

    const response = yield fetch(
      `${wizardURL}/wizard/chat/history/${threadId}`, 
      {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}` 
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to load messages: ${response.status}`);
    }

    const payload = yield response.json();
    const threadMessages = Array.isArray(payload.messages) ? payload.messages : [];

    const transformedMessages = threadMessages.map((msg, index) => ({
      id: msg.message_id || `${threadId}_${index}`,
      text: msg.content || "", 
      sender: msg.role === "assistant" || msg.role === "bot" ? "bot" : "user", 
      timestamp: msg.created_at || new Date().toISOString(),
      metadata: msg.metadata || {},
      files: msg.files || [],
    }));

    yield put(loadThreadMessagesSuccess(transformedMessages));
  } catch (error) {
    console.error("Error loading thread messages:", error);
    yield put(loadThreadMessagesFailure(error.message));
  }
}

export function* watchChatbotSaga() {
  yield takeLatest(sendMessageRequest, sendMessageSaga);
  yield takeLatest(loadThreadMessagesRequest, loadThreadMessagesSaga);
}